import React from "react";
import { Row, Col, Form, InputGroup, Table } from "react-bootstrap";
import Scrollbars from "react-custom-scrollbars-2";

export const CartSummary = ({
    t,
    discountType,
    onDiscountTypeChange,
    discountValue,
    onDiscountValueChange,
    totals,
    getCurrencySymbol,
    disableScrollProps,
    posTaxType,
    pharmacyStateCode,
    patientStateCode,
    cart,
    itemTaxes
}) => {
    const isSameState =
        !patientStateCode ||
        !pharmacyStateCode ||
        pharmacyStateCode === patientStateCode;


    // Function to group items by tax percentage and calculate tax breakdown
    const getTaxBreakdown = () => {
        if (!cart || !itemTaxes) return [];

        const taxGroups = {};
        let totalTaxableBeforeDiscount = 0;

        // First pass: group by tax rate and sum proportional taxable amounts
        cart.forEach(item => {
            const itemKey = `${item._id}-${item.selectedBatchId}`;
            const taxInfo = itemTaxes[itemKey];

            if (!taxInfo) return;

            const taxRate = taxInfo.taxPercentage || 0;
            const returnQuantity = item.returnQuantity || 0;

            if (returnQuantity === 0) return;

            if (!taxGroups[taxRate]) {
                taxGroups[taxRate] = {
                    taxRate: taxRate,
                    taxableAmountBeforeDiscount: 0,
                    itemCount: 0,
                    CGST: 0,
                    SGST: 0,
                    IGST: 0,
                    VAT: 0,
                    totalTax: 0
                };
            }

            // Proportional split for return quantities
            const proportion = returnQuantity / (item.saleQuantity || item.quantity || 1);
            const itemTaxableAmount = taxInfo.taxableAmount * proportion;

            taxGroups[taxRate].taxableAmountBeforeDiscount += itemTaxableAmount;
            taxGroups[taxRate].itemCount += 1;
            totalTaxableBeforeDiscount += itemTaxableAmount;
        });

        // Second pass: distribute bill discount across groups,
        // and recalculate taxes based on taxableAmountAfterDiscount
        return Object.values(taxGroups)
            .sort((a, b) => a.taxRate - b.taxRate)
            .map(group => {
                const proportion = totalTaxableBeforeDiscount > 0 ? group.taxableAmountBeforeDiscount / totalTaxableBeforeDiscount : 0;

                // Distribute discount only to taxable value
                const groupDiscountAmount = (totals.discount || 0) * proportion;
                const netGroupTaxable = Math.max(0, group.taxableAmountBeforeDiscount - groupDiscountAmount);

                let groupTax = 0;
                let groupCGST = 0;
                let groupSGST = 0;
                let groupIGST = 0;
                let groupVAT = 0;

                if (posTaxType === "gst") {
                    if (isSameState) {
                        groupTax = (netGroupTaxable * group.taxRate) / 100;
                        groupCGST = groupTax / 2;
                        groupSGST = groupTax / 2;
                    } else {
                        groupTax = (netGroupTaxable * group.taxRate) / 100;
                        groupIGST = groupTax;
                    }
                } else if (posTaxType === "vat") {
                    groupVAT = (netGroupTaxable * group.taxRate) / 100;
                    groupTax = groupVAT;
                }

                return {
                    ...group,
                    taxableAmountBeforeDiscount: group.taxableAmountBeforeDiscount,
                    taxableAmount: netGroupTaxable,
                    netTaxableAmount: netGroupTaxable,
                    CGST: groupCGST,
                    SGST: groupSGST,
                    IGST: groupIGST,
                    VAT: groupVAT,
                    totalTax: groupTax,
                    totalAmount: netGroupTaxable + groupTax
                };
            });
    };

    const taxBreakdown = getTaxBreakdown();

    // Calculate totals from tax breakdown
    const totalTaxableBeforeDiscountFromBreakdown = taxBreakdown.reduce(
        (sum, g) => sum + (g.taxableAmountBeforeDiscount || 0),
        0
    );
    const totalTaxableFromBreakdown = taxBreakdown.reduce(
        (sum, g) => sum + g.taxableAmount,
        0
    );
    const netTaxableFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.netTaxableAmount, 0);
    const totalCGSTFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.CGST, 0);
    const totalSGSTFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.SGST, 0);
    const totalIGSTFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.IGST, 0);
    const totalVATFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.VAT, 0);
    const totalTaxFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.totalTax, 0);
    const totalAmountFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.totalAmount, 0);

    return (
        <div>
            <Row className="g-2 mb-1 px-1 pb-1 border-bottom">
                {/* <Col md={3}>
                    <Form.Label>{t("Discount Type")}</Form.Label>
                    <Form.Select
                        size="sm"
                        value={discountType}
                        onChange={(event) => onDiscountTypeChange(event.target.value)}
                    >
                        <option value="Fixed">{t("Fixed")}</option>
                        <option value="Percentage">{t("Percentage")} (%)</option>
                    </Form.Select>
                </Col> */}
                <Col md={4} className="d-flex align-items-center gap-2">
                    <Form.Label className="mb-0 text-nowrap">
                        {t("Discount")} {discountType === "Percentage" && "(%)"}
                    </Form.Label>
                    {discountType === "Percentage" ? (
                        <InputGroup size="sm" className="flex-grow-1">
                            <Form.Control
                                type="number"
                                value={discountValue}
                                min={0}
                                max={discountType === "Percentage" ? 100 : undefined}
                                onChange={(event) => {
                                    if (Number(event.target.value) > 0 && Number(event.target.value) <= 100) {
                                        onDiscountValueChange(event.target.value)
                                    } else {
                                        onDiscountValueChange(0)
                                    }
                                }}
                                {...disableScrollProps}
                            />
                            <InputGroup.Text>
                                {getCurrencySymbol()}
                                {totals.discount.toFixed(2)}
                            </InputGroup.Text>
                        </InputGroup>
                    ) : (
                        <Form.Control
                            type="number"
                            size="sm"
                            className="flex-grow-1"
                            min={0}
                            value={discountValue}
                            onChange={(event) => {
                                if (Number(event.target.value) > 0) {
                                    onDiscountValueChange(event.target.value)
                                } else {
                                    onDiscountValueChange(0)
                                }
                            }}
                            {...disableScrollProps}
                        />
                    )}
                </Col>
                {/* <Col md={6} className="align-content-end text-end">
                    <h6 className="m-0 text-danger">
                        {t("Return Net Total")}: {getCurrencySymbol()}
                        {totals.netTotal.toFixed(2)}
                    </h6>
                </Col> */}
            </Row>
            {/* Tax Summary Section */}
            <Scrollbars
                autoHide
                style={{ height: "calc(100vh - 420px)" }}
                universal
                renderTrackHorizontal={(props) => <div {...props} style={{ display: "none" }} />}
            >

                {totals.tax > 0 && (
                    <div className="mt-2">
                        <div className="row">
                            {/* Tax Rate-wise Breakdown Table */}
                            <div className="col-md-12 col-lg-8">
                                {/* <h6 className="mb-3">{t("Tax Breakdown by Rate")}</h6> */}
                                <h6 className="mb-3">{t("Return Tax Summary")}</h6>
                                <Table bordered bsSize="sm" className="mb-4 table-sm">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="text-center">{t("Tax %")}</th>
                                            <th className="text-end">{t("Taxable Value")}</th>
                                            {posTaxType === "gst" ? (
                                                isSameState ? (
                                                    <>
                                                        <th className="text-end">{t("CGST Amt")}</th>
                                                        <th className="text-end">{t("SGST Amt")}</th>
                                                    </>
                                                ) : (
                                                    <th className="text-end">{t("IGST Amt")}</th>
                                                )
                                            ) : (
                                                <th className="text-end">{t("VAT Amt")}</th>
                                            )}
                                            <th className="text-end">{t("Total Tax")}</th>
                                            <th className="text-end">{t("Return Amount")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {taxBreakdown.map((group, index) => (
                                            <tr key={index} className={index % 2 === 0 ? "table-default" : "table-light"}>
                                                <td className="text-center fw-bold">
                                                    {group.taxRate}%
                                                </td>
                                                <td className="text-end">
                                                    {getCurrencySymbol()}{group.taxableAmount.toFixed(2)}
                                                </td>
                                                {posTaxType === "gst" ? (
                                                    isSameState ? (
                                                        <>
                                                            <td className="text-end text-success">
                                                                {getCurrencySymbol()}{group.CGST.toFixed(2)}
                                                            </td>
                                                            <td className="text-end text-success">
                                                                {getCurrencySymbol()}{group.SGST.toFixed(2)}
                                                            </td>
                                                        </>
                                                    ) : (
                                                        <td className="text-end text-success">
                                                            {getCurrencySymbol()}{group.IGST.toFixed(2)}
                                                        </td>
                                                    )
                                                ) : (
                                                    <td className="text-end text-success">
                                                        {getCurrencySymbol()}{group.VAT.toFixed(2)}
                                                    </td>
                                                )}
                                                <td className="text-end fw-bold text-success">
                                                    {getCurrencySymbol()}{group.totalTax.toFixed(2)}
                                                </td>
                                                <td className="text-end fw-bold">
                                                    {getCurrencySymbol()}{group.totalAmount.toFixed(2)}
                                                </td>
                                            </tr>
                                        ))}
                                        {/* Total Row */}
                                        <tr className="table-primary fw-bold">
                                            <td className="text-center">{t("Total")}</td>
                                            <td className="text-end">
                                                {getCurrencySymbol()}{totalTaxableFromBreakdown.toFixed(2)}
                                            </td>
                                            {posTaxType === "gst" ? (
                                                isSameState ? (
                                                    <>
                                                        <td className="text-end">
                                                            {getCurrencySymbol()}{totalCGSTFromBreakdown.toFixed(2)}
                                                        </td>
                                                        <td className="text-end">
                                                            {getCurrencySymbol()}{totalSGSTFromBreakdown.toFixed(2)}
                                                        </td>
                                                    </>
                                                ) : (
                                                    <td className="text-end">
                                                        {getCurrencySymbol()}{totalIGSTFromBreakdown.toFixed(2)}
                                                    </td>
                                                )
                                            ) : (
                                                <td className="text-end">
                                                    {getCurrencySymbol()}{totalVATFromBreakdown.toFixed(2)}
                                                </td>
                                            )}
                                            <td className="text-end text-success">
                                                {getCurrencySymbol()}{totalTaxFromBreakdown.toFixed(2)}
                                            </td>
                                            <td className="text-end">
                                                {getCurrencySymbol()}{totalAmountFromBreakdown.toFixed(2)}
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </div>
                            {/* Final Tax Addition & Grand Total */}
                            <div className="col-md-12 col-lg-4">
                                {/* <h6 className="mb-3">{t("Return Tax Summary")}</h6> */}
                                <Table bordered className="table-sm">
                                    <tbody>
                                        {/* Subtotal */}
                                        <tr>
                                            <td className="text-end">{t("Total Return Taxable Value")}</td>
                                            <td className="text-end fw-bold">
                                                {getCurrencySymbol()}{totalTaxableBeforeDiscountFromBreakdown.toFixed(2)}
                                            </td>
                                        </tr>

                                        {/* discount & Net taxable value */}
                                        {totals.discount > 0 && (
                                            <>
                                                <tr>
                                                    <td className="text-end">{t("Discount")}</td>
                                                    <td className="text-end fw-bold">{getCurrencySymbol()}{totals.discount.toFixed(2)}</td>
                                                </tr>
                                                <tr>
                                                    <td className="text-end">{t("Net Taxable Value")}</td>
                                                    <td className="text-end fw-bold">{getCurrencySymbol()}{netTaxableFromBreakdown.toFixed(2)}</td>
                                                </tr>
                                            </>
                                        )}
                                        {/* Tax Additions */}
                                        {posTaxType === "gst" && isSameState && totalCGSTFromBreakdown > 0 && (
                                            <tr>
                                                <td className="text-end">
                                                    Refund: CGST
                                                    {/* {taxBreakdown.length > 0 && (
                                                    <small className="text-muted ms-1">
                                                        ({((totalCGSTFromBreakdown / totalTaxableFromBreakdown) * 100).toFixed(2)}%)
                                                    </small>
                                                )} */}
                                                </td>
                                                <td className="text-end text-danger">-{getCurrencySymbol()}{totalCGSTFromBreakdown.toFixed(2)}</td>
                                            </tr>
                                        )}
                                        {posTaxType === "gst" && isSameState && totalSGSTFromBreakdown > 0 && (
                                            <tr>
                                                <td className="text-end">
                                                    Refund: SGST
                                                    {/* {taxBreakdown.length > 0 && (
                                                    <small className="text-muted ms-1">
                                                        ({((totalSGSTFromBreakdown / totalTaxableFromBreakdown) * 100).toFixed(2)}%)
                                                    </small>
                                                )} */}
                                                </td>
                                                <td className="text-end text-danger">-{getCurrencySymbol()}{totalSGSTFromBreakdown.toFixed(2)}</td>
                                            </tr>
                                        )}
                                        {posTaxType === "gst" && !isSameState && totalIGSTFromBreakdown > 0 && (
                                            <tr>
                                                <td className="text-end">
                                                    Refund: IGST
                                                    {/* {taxBreakdown.length > 0 && (
                                                    <small className="text-muted ms-1">
                                                        ({((totalIGSTFromBreakdown / totalTaxableFromBreakdown) * 100).toFixed(2)}%)
                                                    </small>
                                                )} */}
                                                </td>
                                                <td className="text-end text-danger">-{getCurrencySymbol()}{totalIGSTFromBreakdown.toFixed(2)}</td>
                                            </tr>
                                        )}
                                        {posTaxType === "vat" && totalVATFromBreakdown > 0 && (
                                            <tr>
                                                <td className="text-end">
                                                    Refund: VAT
                                                    {/* {taxBreakdown.length > 0 && (
                                                    <small className="text-muted ms-1">
                                                        ({((totalVATFromBreakdown / totalTaxableFromBreakdown) * 100).toFixed(2)}%)
                                                    </small>
                                                )} */}
                                                </td>
                                                <td className="text-end text-danger">-{getCurrencySymbol()}{totalVATFromBreakdown.toFixed(2)}</td>
                                            </tr>
                                        )}

                                        {/* Total Tax Row */}
                                        <tr className="table-warning">
                                            <td className="text-end fw-bold">{t("Total Tax Refund")}</td>
                                            <td className="text-end fw-bold text-danger">
                                                -{getCurrencySymbol()}{totalTaxFromBreakdown.toFixed(2)}
                                            </td>
                                        </tr>

                                        {/* Grand Total */}
                                        <tr className="table-danger">
                                            <td className="text-end fw-bold">{t("Return Amount")}</td>
                                            <td className="text-end fw-bold fs-6 text-danger">
                                                {getCurrencySymbol()}{totalAmountFromBreakdown.toFixed(2)}
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>

                                {/* Additional Info */}
                                {/* <div className="mt-3">
                                <small className="text-muted d-block">
                                    <strong>{t("Tax Type")}:</strong> {posTaxType.toUpperCase()}
                                </small>
                                <small className="text-muted d-block">
                                    <strong>{t("States")}:</strong> {pharmacyStateCode || "N/A"} 
                                    <span className={isSameState ? "text-success ms-1" : "text-danger ms-1"}>
                                        {isSameState ? "=" : "≠"}
                                    </span> 
                                    {patientStateCode || "N/A"}
                                </small>
                                {taxBreakdown.length > 1 && (
                                    <small className="text-muted d-block mt-1">
                                        <strong>{t("Tax Rates Applied")}:</strong>{" "}
                                        {taxBreakdown.map((g, i) => (
                                            <span key={i}>
                                                {g.taxRate}%{i < taxBreakdown.length - 1 ? ', ' : ''}
                                            </span>
                                        ))}
                                    </small>
                                )}
                            </div> */}
                            </div>
                        </div>
                    </div>
                )}
            </Scrollbars>

            {/* Final Total Summary */}
            {/* <Row className="mt-3">
                <Col md={12}>
                    <div className="border rounded p-3 bg-danger text-white">
                        <Row>
                            <Col md={4}>
                                <div className="d-flex justify-content-between">
                                    <span>{t("Return Subtotal")}:</span>
                                    <span>{getCurrencySymbol()}{totals.subtotal.toFixed(2)}</span>
                                </div>
                                {totals.discount > 0 && (
                                    <div className="d-flex justify-content-between">
                                        <span>{t("Discount")}:</span>
                                        <span>-{getCurrencySymbol()}{totals.discount.toFixed(2)}</span>
                                    </div>
                                )}
                            </Col>
                            <Col md={4} className="text-center">
                                {totals.tax > 0 && (
                                    <div className="d-flex justify-content-between">
                                        <span>{t("Tax Refund")}:</span>
                                        <div>
                                            <span>-{getCurrencySymbol()}{totalTaxFromBreakdown.toFixed(2)}</span>
                                            {taxBreakdown.length > 0 && (
                                                <small className="ms-2 opacity-75">
                                                    ({((totalTaxFromBreakdown / totals.subtotal) * 100).toFixed(2)}%)
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </Col>
                            <Col md={4} className="text-end">
                                <h4 className="m-0">
                                    {t("Return Amount")}: {getCurrencySymbol()}{totalAmountFromBreakdown.toFixed(2)}
                                </h4>
                                {taxBreakdown.length > 0 && (
                                    <small className="opacity-75">
                                        {t("Includes tax refund")}: -{getCurrencySymbol()}{totalTaxFromBreakdown.toFixed(2)}
                                    </small>
                                )}
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row> */}
        </div>
    );
};

export default CartSummary;